import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type { FixationDetectionConfig } from "../../types/gazeHeatmapTypes";

interface ScanpathSettingsProps {
  config: FixationDetectionConfig;
  onChange: (config: FixationDetectionConfig) => void;
  disabled?: boolean;
}

/**
 * Tooltip component for parameter help
 */
const HelpTooltip: React.FC<{ text: string }> = ({ text }) => {
  const [show, setShow] = useState(false);

  return (
    <div className="relative inline-block ml-1">
      <button
        type="button"
        className="text-gray-400 hover:text-blue-500 transition-colors"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={(e) => {
          e.preventDefault();
          setShow(!show);
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-3.5 w-3.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </button>
      {show && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-800 rounded-lg shadow-lg w-52 text-left">
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
            <div className="w-2 h-2 bg-gray-800 rotate-45" />
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Settings panel for scanpath visualization parameters
 * Only visible when scanpath mode is active
 */
const ScanpathSettings: React.FC<ScanpathSettingsProps> = ({
  config,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const handleSensitivityChange = (value: number) => {
    // Convert slider value (1-20) to display threshold
    // 20 = high sensitivity (show all, threshold=0)
    // 1 = low sensitivity (only strong fixations, threshold=0.8)
    // Linear mapping: slider 1→0.8, slider 20→0
    const displayThreshold = ((20 - value) / 19) * 0.8; // 0 to 0.8
    onChange({ ...config, displayThreshold });
  };

  const handleMergeRadiusChange = (value: number) => {
    onChange({ ...config, mergeRadius: value });
  };

  // Convert displayThreshold to slider value (1-20)
  const thresholdToSlider = (threshold: number): number => {
    // threshold 0 → slider 20, threshold 0.8 → slider 1
    const value = 20 - (threshold / 0.8) * 19;
    return Math.max(1, Math.min(20, Math.round(value)));
  };

  const sensitivityValue = thresholdToSlider(config.displayThreshold ?? 0);

  return (
    <div className="relative">
      {/* Settings toggle button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`p-1.5 rounded-md transition-colors ${
          isOpen
            ? "bg-blue-100 text-blue-600"
            : "text-gray-500 hover:text-blue-600 hover:bg-blue-50"
        } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        title={t("statsNew.scanpath.settings")}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      </button>

      {/* Settings panel */}
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-blue-200 z-50">
          {/* Header */}
          <div className="px-4 py-3 bg-blue-50 border-b border-blue-100">
            <div className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                />
              </svg>
              <span className="text-sm font-semibold text-blue-800">
                {t("statsNew.scanpath.settingsTitle")}
              </span>
            </div>
            <p className="text-xs text-blue-600 mt-1">
              {t("statsNew.scanpath.settingsDescription")}
            </p>
          </div>

          {/* Settings controls */}
          <div className="p-4 space-y-4">
            {/* Sensitivity slider */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <label className="text-sm font-medium text-gray-700">
                    {t("statsNew.scanpath.sensitivity")}
                  </label>
                  <HelpTooltip text={t("statsNew.scanpath.tooltipSensitivity")} />
                </div>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                  {sensitivityValue}/20
                </span>
              </div>
              <input
                type="range"
                min="1"
                max="20"
                value={sensitivityValue}
                onChange={(e) => handleSensitivityChange(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>{t("statsNew.scanpath.fewerFixations")}</span>
                <span>{t("statsNew.scanpath.moreFixations")}</span>
              </div>
            </div>

            {/* Merge radius slider */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <label className="text-sm font-medium text-gray-700">
                    {t("statsNew.scanpath.mergeRadius")}
                  </label>
                  <HelpTooltip text={t("statsNew.scanpath.tooltipMergeRadius")} />
                </div>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                  {config.mergeRadius ?? 1}
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="4"
                value={config.mergeRadius ?? 1}
                onChange={(e) => handleMergeRadiusChange(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>{t("statsNew.scanpath.noMerge")}</span>
                <span>{t("statsNew.scanpath.aggressiveMerge")}</span>
              </div>
            </div>
          </div>

          {/* Footer with reset button */}
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
            <button
              onClick={() =>
                onChange({
                  displayThreshold: 0,
                  mergeRadius: 1,
                })
              }
              className="text-xs text-gray-500 hover:text-blue-600 transition-colors"
            >
              {t("statsNew.scanpath.resetDefaults")}
            </button>
          </div>
        </div>
      )}

      {/* Backdrop to close panel */}
      {isOpen && <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />}
    </div>
  );
};

export default ScanpathSettings;
